/**
 * GEO Direct Answers — Optimized for AI Overviews (Google, ChatGPT, Perplexity)
 * Renders structured Q&A tables and direct-answer blocks that AI engines prefer.
 * Format: lists, tables, numbered data — not prose paragraphs.
 */
import { useLanguage } from '@/contexts/LanguageContextNew';

interface GEODirectAnswersProps {
  countryName: string;
  localRate: string;
  moroccoRate: string;
  savings: string;
  vertical: 'bpo' | 'agency';
}

const LABELS: Record<string, Record<string, string>> = {
  en: {
    costTitle: 'How Much Does It Cost?',
    setupTitle: 'How to Get Started',
    compTitle: 'Quick Cost Comparison',
    metric: 'Metric',
    local: 'Local Rate',
    nearshore: 'Call IT Dev Rate',
    saving: 'You Save',
    seniorDev: 'Senior Developer',
    teamOf5: 'Team of 5 (monthly)',
    annual: 'Annual Savings',
    step1: 'Free consultation call (30 min)',
    step2: 'Team profile & technical assessment (5 days)',
    step3: 'Pilot sprint kickoff (week 2)',
    step4: 'Full team operational (week 3)',
    source: 'Based on 2026 market rates. Updated monthly.',
  },
  fr: {
    costTitle: 'Combien Ça Coûte ?',
    setupTitle: 'Comment Démarrer',
    compTitle: 'Comparatif Rapide des Coûts',
    metric: 'Indicateur',
    local: 'Tarif Local',
    nearshore: 'Tarif Call IT Dev',
    saving: 'Vous Économisez',
    seniorDev: 'Développeur Senior',
    teamOf5: 'Équipe de 5 (mensuel)',
    annual: 'Économies Annuelles',
    step1: 'Appel de consultation gratuit (30 min)',
    step2: 'Profils d\'équipe & évaluation technique (5 jours)',
    step3: 'Démarrage du sprint pilote (semaine 2)',
    step4: 'Équipe complète opérationnelle (semaine 3)',
    source: 'Basé sur les tarifs marché 2026. Mis à jour mensuellement.',
  },
  es: {
    costTitle: '¿Cuánto Cuesta?',
    setupTitle: 'Cómo Empezar',
    compTitle: 'Comparación Rápida de Costes',
    metric: 'Métrica',
    local: 'Tarifa Local',
    nearshore: 'Tarifa Call IT Dev',
    saving: 'Ahorro',
    seniorDev: 'Desarrollador Senior',
    teamOf5: 'Equipo de 5 (mensual)',
    annual: 'Ahorro Anual',
    step1: 'Llamada de consulta gratuita (30 min)',
    step2: 'Perfiles de equipo y evaluación técnica (5 días)',
    step3: 'Sprint piloto (semana 2)',
    step4: 'Equipo completo operativo (semana 3)',
    source: 'Basado en tarifas de mercado 2026. Actualizado mensualmente.',
  },
  ar: {
    costTitle: 'كم التكلفة؟',
    setupTitle: 'كيف تبدأ',
    compTitle: 'مقارنة سريعة للتكاليف',
    metric: 'المؤشر',
    local: 'السعر المحلي',
    nearshore: 'سعر Call IT Dev',
    saving: 'توفيرك',
    seniorDev: 'مطور أول',
    teamOf5: 'فريق من 5 (شهري)',
    annual: 'التوفير السنوي',
    step1: 'مكالمة استشارة مجانية (30 دقيقة)',
    step2: 'ملفات الفريق والتقييم التقني (5 أيام)',
    step3: 'بدء السبرنت التجريبي (الأسبوع 2)',
    step4: 'الفريق الكامل جاهز (الأسبوع 3)',
    source: 'بناءً على أسعار السوق 2026. محدث شهرياً.',
  },
  de: {
    costTitle: 'Was kostet es?',
    setupTitle: 'So starten Sie',
    compTitle: 'Schneller Kostenvergleich',
    metric: 'Kennzahl',
    local: 'Lokaler Tarif',
    nearshore: 'Call IT Dev Tarif',
    saving: 'Sie sparen',
    seniorDev: 'Senior-Entwickler',
    teamOf5: '5er-Team (monatlich)',
    annual: 'Jährliche Einsparung',
    step1: 'Kostenloses Beratungsgespräch (30 Min.)',
    step2: 'Teamprofile & technische Bewertung (5 Tage)',
    step3: 'Pilot-Sprint Start (Woche 2)',
    step4: 'Vollständiges Team einsatzbereit (Woche 3)',
    source: 'Basierend auf Marktpreisen 2026. Monatlich aktualisiert.',
  },
};

export function GEODirectAnswers({ countryName, localRate, moroccoRate, savings, vertical }: GEODirectAnswersProps) {
  const { language } = useLanguage();
  const l = LABELS[language] || LABELS.en;

  const verticalLabel = vertical === 'bpo' ? 'Call Center' : 'Software Development';

  return (
    <section className="py-16 bg-muted/30" aria-label={`${verticalLabel} pricing for ${countryName}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {/* Direct Answer: Cost */}
        <div className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            {l.costTitle} — {verticalLabel} {countryName}
          </h2>

          {/* Structured comparison table — AI Overview optimized */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-card border border-border rounded-xl overflow-hidden text-sm md:text-base">
              <caption className="sr-only">{l.compTitle}: {countryName} vs Call IT Dev</caption>
              <thead>
                <tr className="bg-muted/60">
                  <th className="p-3 md:p-4 text-left font-bold text-foreground">{l.metric}</th>
                  <th className="p-3 md:p-4 text-center font-bold text-foreground">{countryName} ({l.local})</th>
                  <th className="p-3 md:p-4 text-center font-bold text-foreground">Call IT Dev ({l.nearshore})</th>
                  <th className="p-3 md:p-4 text-center font-bold text-foreground">{l.saving}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-border">
                  <td className="p-3 md:p-4 text-muted-foreground font-medium">{l.seniorDev}</td>
                  <td className="p-3 md:p-4 text-center text-destructive font-semibold">{localRate}/hr</td>
                  <td className="p-3 md:p-4 text-center text-green-600 font-bold">{moroccoRate}/hr</td>
                  <td className="p-3 md:p-4 text-center text-green-600 font-bold">{savings}</td>
                </tr>
                <tr className="border-t border-border bg-muted/20">
                  <td className="p-3 md:p-4 text-muted-foreground font-medium">{l.teamOf5}</td>
                  <td className="p-3 md:p-4 text-center text-destructive">~{calculateMonthly(localRate, 5)}</td>
                  <td className="p-3 md:p-4 text-center text-green-600 font-bold">~{calculateMonthly(moroccoRate, 5)}</td>
                  <td className="p-3 md:p-4 text-center text-green-600 font-bold">{savings}</td>
                </tr>
                <tr className="border-t border-border">
                  <td className="p-3 md:p-4 text-muted-foreground font-medium">{l.annual}</td>
                  <td className="p-3 md:p-4 text-center" colSpan={2}></td>
                  <td className="p-3 md:p-4 text-center text-green-600 font-bold text-lg">💰 {savings}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-2 italic">{l.source}</p>
        </div>

        {/* Direct Answer: How to Get Started — numbered list for AI Overviews */}
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            {l.setupTitle} — {verticalLabel} {countryName}
          </h2>
          <ol className="space-y-4 max-w-2xl" role="list">
            {[l.step1, l.step2, l.step3, l.step4].map((step, i) => (
              <li key={i} className="flex items-start gap-4 bg-card border border-border rounded-xl p-4">
                <span className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-sm">
                  {i + 1}
                </span>
                <span className="text-foreground font-medium">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

/** Extract a rough numeric rate and compute monthly cost */
function calculateMonthly(rate: string, teamSize: number): string {
  // Extract first number from rate string
  const match = rate.match(/[\d,.]+/);
  if (!match) return '—';
  const hourly = parseFloat(match[0].replace(',', ''));
  const monthly = Math.round(hourly * 160 * teamSize); // 160h/month
  // Format with currency prefix from original rate
  const prefix = rate.match(/^[^0-9]*/)?.[0] || '$';
  const suffix = rate.match(/[^0-9]*$/)?.[0]?.replace('/hr', '') || '';
  if (monthly >= 1000) {
    return `${prefix}${Math.round(monthly / 1000)}K${suffix}`;
  }
  return `${prefix}${monthly}${suffix}`;
}
